// The layered rider and parked bike (bike plan 3, task 5): the layers bikeRiderLayout.mjs lists, drawn from the
// atlases bikeArt.js loads, with the legs and the spoke discs driven by bikeRide.mjs's rideTick. The review page's
// draw() (scripts/anim/bike/review_ride.html) as Phaser objects.
//
// The position is the centre of a box `displayHeight` tall whose bottom is the ground line, so x/y keep the old
// bike sprite's "centre" meaning for BikeController and the scenes. Inside it:
//   _flip  at the ground point, scale (±s, s): the mirrored rows;
//   _tilt  rotated by the stop tilt (nested inside the flip, its sign needs no correction);
//   the layers, each an Image whose origin is the ground point of its 1254-px canvas, and per wheel a Container at
//   the wheel's centre, turned to the ellipse and squashed to it, holding the disc turned by the spin.
// It ticks itself on the scene's update: callers only say how fast it goes (setRide) and where it faces.
import Phaser from 'phaser';
import { riderLayers, parkedLayers, wheelsFor, viewFlip, rowFor } from './bikeRiderLayout.mjs';
import { rideTick, RIDE } from './bikeRide.mjs';
import { spriteArtKeys, riderHair, riderHairFallback } from './bikeArtKeys.mjs';
import { ensureBikeArt } from './bikeArt.js';
import { applyLook, ensureMasks } from '../look/applyLook.js';
import { isDefaultLook } from '../look/lookCatalog.mjs';
import { loadHair, itemLift, ITEM_FIT } from '../look/hairArt.mjs';
import { dir8FromVector } from './dir8.mjs';

const BOX_W = 1.6;        // the box's width, of its height

export class BikeSprite extends Phaser.GameObjects.Container {
  constructor(scene, x, y, { type, design = null, char = null, items = [], dir8 = 4, parked = false, visible = true } = {}) {
    super(scene, x, y);
    this.rig = scene.cache.json.get('bike_rig');
    this._type = type; this._design = design; this._char = char; this._items = items;
    this._look = null;    // the rider's skin / eyes / hair (setLook)
    this._hairMissing = new Set();   // hair atlases that failed to load: not asked for again for 10 s
    this._hairDrawn = null;          // the hair atlases loadHair picked last: drawn until the next pick lands
    this._hairAsk = 0;
    this._parked = parked || !char;
    this._dir8 = ((dir8 % 8) + 8) % 8;
    this._ride = { speed: 0, pedalling: false };
    this._st = { turns: 0, wheel: 0, stopped: true, tilt: 0, frame: RIDE.STOP_FRAME };
    this._h = 1; this._s = 1;
    this._images = []; this._wheels = [];
    this._drawn = '';
    this._flip = new Phaser.GameObjects.Container(scene, 0, 0);
    this._tilt = new Phaser.GameObjects.Container(scene, 0, 0);
    this._flip.add(this._tilt);
    this.add(this._flip);
    scene.add.existing(this);
    this._onUpdate = (_t, dtMs) => this.update(dtMs);
    scene.events.on(Phaser.Scenes.Events.UPDATE, this._onUpdate);
    this.once(Phaser.GameObjects.Events.DESTROY, () => scene.events.off(Phaser.Scenes.Events.UPDATE, this._onUpdate));
    this.setDisplayHeight(this._h);
    super.setVisible(visible);   // built hidden, it loads its art only once shown
    this._load();
  }

  setVisible(value) {
    const shown = !!value && !this.visible;
    super.setVisible(value);
    if (shown) this._load();
    return this;
  }

  // ---- what it shows ----------------------------------------------------------------------------

  setRider(char, items = []) {
    if (char !== this._char) this._hairDrawn = null;
    this._char = char; this._items = items;
    if (!char) this._parked = true;
    this._load();
    return this;
  }

  /** The rider's look (look/): its rider and clothes pieces recolour; the masks load only for a look that needs them. */
  setLook(look) {
    this._look = look ?? null;
    this._drawn = '';
    this._load();
    return this;
  }

  setDesign(type, design) {
    const typeChanged = type !== this._type;
    this._type = type; this._design = design;
    if (typeChanged) this.setDisplayHeight(this._h);
    this._load();
    return this;
  }

  setParked(parked) {
    const was = this._parked;
    this._parked = parked || !this._char;
    if (was !== this._parked) this.setDisplayHeight(this._h);
    if (this._parked) { this._tilt.rotation = 0; this._st = { ...this._st, tilt: 0 }; }
    this._load();
    return this;
  }

  get parked() { return this._parked; }

  // ---- where it faces (NatalkaSprite's API, so WalkController drives it unchanged) ----------------

  get facing8() { return this._dir8; }

  setFacing8(dir8) {
    this._dir8 = ((dir8 % 8) + 8) % 8;
    this._redraw();
    return this;
  }

  setMotion(dx, dy) {
    if (dx !== 0 || dy !== 0) this.setFacing8(dir8FromVector(dx, dy));
    return this;
  }

  // ---- how it moves -------------------------------------------------------------------------------

  setRide({ speed, pedalling }) {
    this._ride = { speed: Math.max(0, speed || 0), pedalling: !!pedalling };
    return this;
  }

  // the stop pose at once: foot down, tilted
  setStop() {
    this._ride = { speed: 0, pedalling: false };
    this._st = { ...this._st, stopped: true, frame: RIDE.STOP_FRAME, tilt: this._view().stopTilt };
    this._redraw();
    return this;
  }

  update(dtMs) {
    if (!this.rig || this._parked || !this.active) return;
    const v = this._view();
    this._st = rideTick(this._st, { ...this._ride, dt: Math.min(dtMs, 100) / 1000,
                                     wheelRadiusPx: this._wheelRadius() * this._s,
                                     spokes: this.rig.spokes?.[this._type] ?? 18, stopTilt: v.stopTilt });
    this._tilt.rotation = this._st.tilt * Math.PI / 180;
    for (const w of this._wheels) w.disc.rotation = this._st.wheel;
    this._redraw();
  }

  // ---- size -----------------------------------------------------------------------------------------

  // h: the box's height in screen px (the rider's head, or the parked bike's top, to the ground)
  setDisplayHeight(h) {
    this._h = h;
    const px = this._parked ? (this.rig?.parkedHeightPx?.[this._type] ?? this.rig?.parkedHeightPx?.pink)
                            : this.rig?.riderHeightPx;
    this._s = px ? h / px : 1;
    this._flip.setPosition(0, h / 2);
    this._flip.setScale(this._flipX() * this._s, this._s);
    // the box (what a click hits): the content's height, and a bike is longer than it is tall
    this.setSize(h * BOX_W, h);
    this.input?.hitArea?.setSize(h * BOX_W, h);
    return this;
  }

  get displayHeight() { return this._h; }

  // ---- internals ------------------------------------------------------------------------------------

  _flipX() { return viewFlip(this._dir8).flip ? -1 : 1; }

  // the row drawn: the rider's (a row they lack falls back), or the bike's own when parked
  _row() {
    const { view } = viewFlip(this._dir8);
    if (this._parked) return this.rig.views[view] ? view : 'front34';
    return rowFor(this.rig, this._char, view);
  }

  _view() { return this.rig.views[this._row()]; }

  _wheelRadius() {
    const rows = this.rig.wheels?.[this._type] ?? {};
    return (rows[this._row()]?.[0] ?? rows.side?.[0])?.radius ?? 100;
  }

  _keys() {
    return spriteArtKeys({ rig: this.rig, shown: this.visible, parked: this._parked, char: this._char,
                           type: this._type, design: this._design, items: this._items, look: this._look });
  }

  // The rider's hair to ask for: their style's, the default style's while theirs is missing (it failed to load).
  _hairWanted() {
    const want = riderHair(this._char, this._look, this._items);
    return this._hairMissing.has(want.front) ? riderHairFallback(this._char) : want;
  }

  _missHair(k) { this._hairMissing.add(k); setTimeout(() => this._hairMissing.delete(k), 10000); }

  // The bike and the rider's body and clothes, and beside them the hair through loadHair: a style whose atlas does
  // not load draws the default style's, and one that failed is not asked for again for 10 s, as in the walk. The
  // hair drawn is the pick, kept until the next one lands (when not even the default's load, the old one stays).
  _load() {
    if (!this.rig) { console.warn('[BikeSprite] bike_rig.json not loaded'); return; }
    const all = this._keys();
    const keys = all.filter((k) => !k.startsWith('bike_hair_'));
    const hair = all.length > keys.length ? this._hairWanted() : null;
    const ask = ++this._hairAsk;
    let left = 2, got = null;
    const settled = () => {
      if (--left || !this.active) return;
      const draw = () => { if (this.active) { this._drawn = ''; this._redraw(); } };
      const masked = got ? [...keys, got.front] : keys;
      if (this._char && !isDefaultLook(this._char, this._look)) ensureMasks(this.scene, masked, draw);
      else draw();
    };
    ensureBikeArt(this.scene, keys, settled);
    if (!hair || this._hairMissing.has(hair.front)) { settled(); return; }
    loadHair((k, done) => ensureBikeArt(this.scene, k, done), (k) => this.scene.textures.exists(k), hair, riderHairFallback(this._char), (g) => {
      if (g !== hair) this._missHair(hair.front);
      if (g === null) this._missHair(riderHairFallback(this._char).front);
      if (ask === this._hairAsk && g) this._hairDrawn = g;
      got = g;
      settled();
    });
  }

  _has = (atlas, frame) => this.scene.textures.exists(atlas) && this.scene.textures.get(atlas).has(frame);

  _layers() {
    const { rig, _type: type, _design: design, _has: has } = this;
    const view = this._row();
    return this._parked ? parkedLayers({ rig, type, design, view, has })
      : riderLayers({ rig, char: this._char, view, ridden: true, type, design, items: this._items, has, k: this._st.frame,
                      hair: this._hairDrawn, flip: viewFlip(this._dir8).flip, lift: this._lift(view) });
  }

  // {itemId: canvas px} the items worn draw higher over the rider's style seen from `view` (hairArt ITEM_FIT), over its
  // own hair only
  _lift(view) {
    const hair = this._hairDrawn;
    if (!hair || hair.front === riderHairFallback(this._char).front) return {};
    return Object.fromEntries((this._items ?? []).map((it) => [it.id, itemLift('bike', this._char, this._look, it.id, ITEM_FIT, view)])
      .filter(([, up]) => up));
  }

  // Put the layers in order, only when something drawn changed (a leg frame, the row, the rider, the design). The
  // pooled images and wheels stay in _tilt for good (a child a Container lets go of goes back on the scene's display
  // list, drawn loose at the world origin): unused ones hide, and the order is a sort.
  _redraw() {
    if (!this.rig || !this.scene) return;
    this._flip.setScale(this._flipX() * this._s, this._s);
    const layers = this._layers();
    const sig = `${this._row()}|${this._type}|${layers.map((l) => (l.discs ? '*' : `${l.atlas}:${l.frame}${l.dy ? `@${l.dy}` : ''}`)).join(',')}`;
    if (sig === this._drawn) return;
    this._drawn = sig;
    const [gx, gy] = this.rig.ground, c = this.rig.canvas;
    let n = 0, z = 0;
    for (const l of layers) {
      if (l.discs) { for (const box of this._placeWheels()) box._z = z++; continue; }
      let im = this._images[n];
      if (!im) { im = this._images[n] = new Phaser.GameObjects.Image(this.scene, 0, 0, l.atlas, l.frame); this._tilt.add(im); }
      n += 1;
      im.setTexture(l.atlas, l.frame).setOrigin(gx / c, gy / c).setPosition(0, l.dy ?? 0).setVisible(true);
      applyLook(im, this._char, this._look);   // the rider and their clothes; the bike's own pieces have no mask
      im._z = z++;
    }
    for (let i = n; i < this._images.length; i++) { this._images[i].setVisible(false); this._images[i]._z = Infinity; }
    if (!layers.some((l) => l.discs)) for (const w of this._wheels) { w.box.setVisible(false); w.box._z = Infinity; }
    this._tilt.sort('_z');
  }

  // the row's wheels placed (the shown ones returned, in order); the rest hidden
  _placeWheels() {
    const wheels = wheelsFor(this.rig, this._type, this._row()).filter((w) => this._has(w.atlas, w.frame));
    wheels.forEach((w, i) => {
      let slot = this._wheels[i];
      if (!slot) {
        const disc = new Phaser.GameObjects.Image(this.scene, 0, 0, w.atlas, w.frame);
        slot = this._wheels[i] = { box: new Phaser.GameObjects.Container(this.scene, 0, 0, [disc]), disc };
        this._tilt.add(slot.box);
      }
      slot.disc.setTexture(w.atlas, w.frame).setOrigin(0.5).setRotation(this._st.wheel);
      slot.box.setPosition(w.x, w.y).setRotation(w.angle).setScale(1, w.ratio).setVisible(true);
    });
    for (let i = wheels.length; i < this._wheels.length; i++) { this._wheels[i].box.setVisible(false); this._wheels[i].box._z = Infinity; }
    return this._wheels.slice(0, wheels.length).map((w) => w.box);
  }
}
